import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { 
  useTheme, 
  useMediaQuery, 
  Box,   
  Typography, 
  CircularProgress, 
  IconButton, 
  Tooltip, 
} from '@mui/material';
import { 
  Add,
  AddCircleOutline, 
  Edit,
  Update,
  Delete,
  DeleteForever,
  FileUpload,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Airports = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);   
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "Airports", "create", false);
  const canEdit = hasPermission(user?.permissions, "Airports", "update", false);
  const canDelete = hasPermission(user?.permissions, "Airports", "delete", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 

  const [fileName, setFileName] = useState("");
  const [airportData, setAirportData] = useState([]);

  const [airports, setAirports] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    _id: false,
    locationType: false,
    longitude: false,
    latitude: false,
    altitude: false,
    ckNotEFB: false,
    headInfo: false,
    heilideckHeight: false,
    isPossibleDiversionBit: false,
    isPossibleFuelLocation: false,
    magneticVariation: false,
    runwayLength: false,
    taxiDep: false,
    taxiArr: false,
  });

    
  useEffect(() => {
    const handleAirports = async () => {
      try {
        setError(null);
        setLoading(true);
  
        const res = await api.get('/airports');        
        setAirports(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    handleAirports();
  }, []); 


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      ...(isMobile ? { width: 60 } : { flex: 1 }),
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { 
      field: "iataCode", 
      headerName: "IataCode", 
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },
    {
      field: "icaoCode",
      headerName: "IcaoCode",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),  
    },
    {
      field: "airportName",
      headerName: "AirportName",     
      ...(isMobile ? { width: 250 } : { flex: 3 }),  
    },
    {
      field: "countryCode",
      headerName: "CountryCode",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),     
    },
    {
      field: "isMainBase",
      headerName: "IsMainBase",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "locationType",
      headerName: "LocationType",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),      
    },    
    {
      field: "latitude",
      headerName: "Latitude",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "longitude",
      headerName: "Longitude",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "altitude",
      headerName: "Altitude",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "timeZone",
      headerName: "TimeZone",
      align: "right",
      ...(isMobile ? { width: 160 } : { flex: 2 }),  
    },
    {
      field: "timeZoneDiff",
      headerName: "TZDiff",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),     
    },
    {
      field: "travelZone",
      headerName: "TravelZone",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
    },    
    {
      field: "headInfo",
      headerName: "HeadInfo",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),   
    },    
    {
      field: "magneticVariation",
      headerName: "MagneticVariation",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),      
    },
    {
      field: "heilideckHeight",
      headerName: "HeilideckHeight",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),      
    },
    {
      field: "runwayLength",
      headerName: "RunwayLength",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),      
    },
    {
      field: "taxiDep",
      headerName: "TaxiDep",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),      
    },
    {
      field: "taxiArr",
      headerName: "TaxiArr",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),     
    },
    {
      field: "ckNotEFB",
      headerName: "CkNotEFB",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "isPossibleDiversionBit",
      headerName: "IsPossibleDiversionBit",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),       
    },
    {
      field: "isPossibleFuelLocation",
      headerName: "IsPossibleFuelLocation",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),     
    },
  ], [isMobile]);
  
  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: "primary.dark",
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/airports/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: "primary.dark",
                    }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);                  
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate,
    canEdit,
    canDelete,
  ]);
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, airports);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try { 
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/airports/delete/${idToDelete}`);

        setAirports(airports.filter((item) => item._id !== idToDelete));
        toast.success('Airport deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, airports);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/airports/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setAirports(airports.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete airports!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    airports
  ]);
   
  const handleUpload = (event) => {
    const files = event.target.files;
  
    if (files.length === 0) return;
    setFileName(files[0].name);
    setLoading(true);
  
    const file = files[0];
    if (!file.name.endsWith('.json')) {
      toast.error("Please upload a .json file.");
      setLoading(false);
      return;
    }
  
    const reader = new FileReader();
    reader.onload = function (e) {
      try {
        const raw = e.target.result;        
        const parsedJSON = JSON.parse(raw);
          
        // Validate and transform the data into an array
        const data = Object.keys(parsedJSON)?.map((key) => {
          const values = parsedJSON[key];

          return {
            iataCode: values.iata || "",
            icaoCode: values.icao || "",
            airportName: values.name || "",
            countryCode: values.country || "",
            altitude: values.altitude || 0,
            latitude: values.latitude || null,
            longitude: values.longitude || null,
            timeZone: values.tz || "",
          };
        });
        
        setAirportData(data);
        toast.success("File uploaded successfully.");
      } catch (error) {
        toast.error(error?.message || 'Error parsing JSON file!');
      } finally {
        setLoading(false);
      }
    };
  
    reader.onerror = function (error) {
      toast.error('File reading error: ' + error);
      setLoading(false);
    };
  
    reader.readAsText(file);
  };
  
  const handleBulkUpdate = async () => {
    try {
      if (!airportData?.length) {
        toast.error('No airport data to update.');
        return;
      }

      const payload = airports
        ?.filter(ap => !ap.icaoCode) 
        ?.map(ap => {
          const found = airportData.find(
            item => item.iataCode === ap.iataCode
          );

          if (!found) return null;

          return {
            iataCode: ap.iataCode,
            icaoCode: found.icaoCode,
            airportName: found.airportName,
            countryCode: found.countryCode,
            latitude: found.latitude,
            longitude: found.longitude,
            altitude: found.altitude,
            timeZone: found.timeZone,
          };
        })
        .filter(Boolean); 

      if (!payload.length) {
        toast.info('No matching airports found to update.');
        return;
      }

      const url = '/airports/bulk-update';

      await api.put(url, payload);

      toast.success(`Updated ${payload.length} airports successfully.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update airports!');
      console.error(error);
    }
  };

  const handleBulkCreate = useCallback(async () => {
    try {
      const url = '/airports/bulk-create';
      const payload = { startDate, endDate };
      
      const res = await api.post(url, payload);

      if (res.data) {
        setAirports(res.data)
        toast.success(res.data?.message || "Airports created successfully.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to created airports!");
    } 
  }, [startDate, endDate]); 


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Airports" 
          subtitle={`${loading ? 'Loading...' : 'List of Airports'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>          
            <Tooltip title="Create from flights" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    handleBulkCreate();
                  }}
                >
                  <Add />
                </StyledButton>  
              </span>              
            </Tooltip>
            
            <label htmlFor={!!fileName ? undefined : "upload-file"}>
              <Tooltip title="Upload json file" arrow>
                <span>
                  <StyledButton 
                    component="span" 
                    disabled={!!fileName}
                  >
                    <FileUpload />
                  </StyledButton>  
                </span>                
              </Tooltip>
            </label>
            <input
              id="upload-file"
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleUpload}
            /> 

            <Tooltip title="Update from json file" arrow>
              <span>
                <StyledButton 
                  disabled={airportData.length === 0}
                  onClick={() => {
                    if (!canEdit) return;
                    handleBulkUpdate();
                  }}
                >
                  <Update />
                </StyledButton>  
              </span>              
            </Tooltip>

            {!isMobile && (<Tooltip title="Bulk soft delete" arrow>
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, airports)}
                  onClick={() => {
                    if (!canDelete) return;
                    handleBulkDelete();
                  }}
                >
                  <Delete />
                </StyledButton>     
              </span>   
            </Tooltip>)}                       

            <Tooltip title="Create" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    navigate("/airports/new");
                  }}
                >
                  <AddCircleOutline />
                </StyledButton>
              </span>              
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={airports}
                primaryField="iataCode"
                secondaryField="airportName"
                fields={
                  [
                    {label:"ICAO", key:"icaoCode"},
                    {label:"Country", key:"countryCode"},
                    {label:"TimeZone", key:"timeZone"},
                    {label:"TZDiff", key:"timeZoneDiff"},
                    {label:"TravelZone", key:"travelZone"},
                  ]
                }
                isActiveField={null}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/airports/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={airports || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
              }}
            />
            )}
          </Box>    
        </Box>
      )}
      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Airports;
